import * as HLF from "html-living-framework";
import {PuzzleModel, PuzzleType, PuzzleTypes} from "../GameObjects/Puzzle/PuzzleModel";
import {ItemData, ItemModel, ItemType} from "./ItemModel";
import {array, EnumDict} from "../Core/Reflection";
import {StateName} from "./StateName";
import {GameManager} from "../Managers/GameManager";
import {MusicType} from "../GameObjects/Music/MusicModel";
import {CookingModel, CookingType} from "../GameObjects/Cooking/CookingModel";
import {AssemblyModel, AssemblyType} from "../GameObjects/Assembly/AssemblyModel";

const fontGelPenUpright = new HLF.FontAsset("gelPenMedium35");

export const AudioAssets = {
    sfx: {
        "ui_click": new HLF.AudioAsset("ui_click", HLF.AudioCategory.SFX),
        "ui_select": new HLF.AudioAsset("ui_select", HLF.AudioCategory.SFX),

        "knockknock": new HLF.AudioAsset("knockknock", HLF.AudioCategory.SFX),

        "knife-slice": new HLF.AudioAsset("knife-slice", HLF.AudioCategory.SFX),
        "timer-ticks": new HLF.AudioAsset("timer-ticks", HLF.AudioCategory.SFX),
        "timer-ding": new HLF.AudioAsset("timer-ding", HLF.AudioCategory.SFX),
        "chewing": new HLF.AudioAsset("chewing", HLF.AudioCategory.SFX),
        "liquid-pour": new HLF.AudioAsset("liquid-pour", HLF.AudioCategory.SFX),

        "robot-button": new HLF.AudioAsset("robot-button", HLF.AudioCategory.SFX),

        "item-change": new HLF.AudioAsset("item-change", HLF.AudioCategory.SFX),
        "seamonster-water": new HLF.AudioAsset("seamonster-water", HLF.AudioCategory.SFX),

        "change-into": new HLF.AudioAsset("change-into", HLF.AudioCategory.SFX),

        "drop in water": new HLF.AudioAsset("drop in water", HLF.AudioCategory.SFX),

        "oven-close": new HLF.AudioAsset("oven-close", HLF.AudioCategory.SFX),
        "oven-open": new HLF.AudioAsset("oven-open", HLF.AudioCategory.SFX),

        "puzzle-place": new HLF.AudioAsset("puzzle-place", HLF.AudioCategory.SFX),
        "short-succes": new HLF.AudioAsset("short-succes", HLF.AudioCategory.SFX),
        "whoosh": [1, 2, 3].map(it => new HLF.AudioAsset(`whoosh${it}`, HLF.AudioCategory.SFX)),

        "box-in": new HLF.AudioAsset("box-in", HLF.AudioCategory.SFX),
        "box-open": new HLF.AudioAsset("box-open", HLF.AudioCategory.SFX),
    }
}

const packageBackground = new HLF.TextureAsset("package", "images/static/package.jpg");

const assemblyPackages = {
    [AssemblyType.Robot]: [
        ...HLF.AssetFactory({"@1x": 1, "@2x": [1, 2]}, (i) => new HLF.TextureAtlasAsset(`package-robot-${i}`)),
        AudioAssets.sfx["robot-button"],
    ],
    [AssemblyType.Monster]: [
        ...HLF.AssetFactory({"@1x": 1, "@2x": [1, 3]}, (i) => new HLF.TextureAtlasAsset(`package-monster-${i}`)),
        AudioAssets.sfx["item-change"],
        AudioAssets.sfx["seamonster-water"],
    ],
    [AssemblyType.Movie]: [
        ...HLF.AssetFactory({"@1x": [1, 2], "@2x": [1, 6]}, (i) => new HLF.TextureAtlasAsset(`package-movie-${i}`)),
        ...HLF.AssetFactory({"@1x": 1, "@2x": [1, 2]}, (i) => new HLF.TextureAtlasAsset(`magic-poof-${i}`)),
        new HLF.TextureAsset("package-movie", "images/static/package-movie.jpg"),
        new HLF.TextureAsset("package-movie-tv", "images/static/package-movie-tv.jpg"),
        AudioAssets.sfx["change-into"],
    ]
}
const assemblyBackground = {
    [AssemblyType.Robot]: new HLF.TextureAsset("package-metal", "images/static/package-metal.jpg"),
    [AssemblyType.Monster]: packageBackground,
    [AssemblyType.Movie]: packageBackground,
};

const puzzleItems = PuzzleTypes.reduce((a, t) => {
    a[t] = HLF.AssetFactory({"@1x": 1, "@2x": 1}, (i) => new HLF.TextureAtlasAsset(`puzzle-${t}-${i}`));
    return a;
}, {} as EnumDict<PuzzleType, HLF.IAsset<any>[]>);
const puzzleImages = PuzzleTypes.reduce((a, t) => {
    a[t] = new HLF.TextureAsset(`puzzle-${t}`, `images/static/puzzle-${t}.png`);
    return a;
}, {} as EnumDict<PuzzleType, HLF.IAsset<any>>);
const puzzleFrames = [1, 2, 3, 4, 5].reduce((a, t) => {
    a[t] = new HLF.TextureAsset(`puzzle-frame-${t}`, `images/static/puzzle-frame-${t}.png`);
    return a;
}, {} as EnumDict<number, HLF.IAsset<any>>);

const cookingAssets: EnumDict<CookingType, HLF.IAsset<any>[]> = {
    [CookingType.Durian]: [
        ...HLF.AssetFactory({"@1x": 1, "@2x": 1}, i => new HLF.TextureAtlasAsset(`package-durian-${i}`)),
        AudioAssets.sfx["knife-slice"],
        AudioAssets.sfx["timer-ticks"],
        AudioAssets.sfx["timer-ding"],
        AudioAssets.sfx["chewing"],
        AudioAssets.sfx["liquid-pour"],
    ],
    [CookingType.Pasta]: [
        ...HLF.AssetFactory({"@1x": 1, "@2x": [1, 2]}, i => new HLF.TextureAtlasAsset(`package-pasta-${i}`)),
        ...[1, 2, 3, 4].map(i => new HLF.TextureAsset(`noodles-color-${i}`, `images/static/pasta/pasta-colour${i}.png`)),
        ...[1, 2, 3, 4].map(i => new HLF.TextureAsset(`sauce-color-${i}`, `images/static/pasta/sauce-colour${i}.png`)),
        AudioAssets.sfx["drop in water"],
        AudioAssets.sfx["timer-ticks"],
        AudioAssets.sfx["timer-ding"],
        AudioAssets.sfx["chewing"],
    ],
    [CookingType.Delight]: [
        ...HLF.AssetFactory({"@1x": 1, "@2x": 1}, i => new HLF.TextureAtlasAsset(`package-delight-${i}`)),
        ...[0, 1, 2, 3, 4].map(i => new HLF.TextureAsset(`delight-color-${i}`, `images/static/delight/turkish-colour${i}.png`)),
        AudioAssets.sfx["oven-close"],
        AudioAssets.sfx["oven-open"],
        AudioAssets.sfx["timer-ticks"],
        AudioAssets.sfx["timer-ding"],
        AudioAssets.sfx["chewing"],
        AudioAssets.sfx["knife-slice"],
        AudioAssets.sfx["liquid-pour"],
    ],
    [CookingType.Coconut]: [
        ...HLF.AssetFactory({"@1x": 1, "@2x": 1}, i => new HLF.TextureAtlasAsset(`package-coconut-${i}`)),
        AudioAssets.sfx["knife-slice"],
        AudioAssets.sfx["liquid-pour"],
    ]
};

const luna = new HLF.SpineAsset("luna", "spine/luna@x.json");

export const Assets = {
    boot: [
        new HLF.TextureAtlasAsset("boot-1"),
        new HLF.TextureAsset("splash", "images/static/splash.jpg"),
        fontGelPenUpright,
    ],
    fontGelPenUpright,
    common: [
        ...HLF.AssetFactory({"@1x": 1, "@2x": 1}, (i) => new HLF.TextureAtlasAsset(`ui-${i}`)),
        AudioAssets.sfx.ui_select,
        AudioAssets.sfx.ui_click
    ],
    intro: [
        new HLF.TextureAsset("intro", "images/static/intro.jpg"),
        ...HLF.AssetFactory({"@1x": 1, "@2x": [1, 3]}, (i) => new HLF.TextureAtlasAsset(`intro-${i}`)),
        luna
    ],
    home: [
        ...[1, 2, 3, 4].map(i => new HLF.TextureAsset(`home_0${i}`, `images/static/home_0${i}.jpg`)),
        ...HLF.AssetFactory({"@1x": [1, 2], "@2x": [1, 5]}, (i) => new HLF.TextureAtlasAsset(`home-${i}`)),
        ...array(i => i + 1, 7).map(it => new HLF.AudioAsset(`${MusicType.Glockenspiel}-${it}`, HLF.AudioCategory.SFX)),
        ...["start", "loop", "end"].map(it => new HLF.AudioAsset(`${MusicType.Bagpipes}-${it}`, HLF.AudioCategory.SFX)),
        new HLF.AudioAsset(`${MusicType.Didgeridoo}`, HLF.AudioCategory.SFX),
        new HLF.AudioAsset(`${MusicType.Didgeridoo}-end`, HLF.AudioCategory.SFX),
        new HLF.AudioAsset(`${MusicType.Nyatiti}`, HLF.AudioCategory.SFX),
        AudioAssets.sfx["knockknock"],
        AudioAssets.sfx["box-in"],
        AudioAssets.sfx["box-open"],
        luna,
    ],
    puzzleMiniGame: [
        new HLF.TextureAsset("package-puzzle", "images/static/package-puzzle.jpg"),
        ...HLF.AssetFactory({"@1x": 1, "@2x": 1}, (i) => new HLF.TextureAtlasAsset(`package-puzzle-${i}`)),
        ...array((k, v) => v!, puzzleFrames),
        AudioAssets.sfx["puzzle-place"],
        AudioAssets.sfx["short-succes"],
        ...AudioAssets.sfx["whoosh"],
    ],
    cookingMiniGame: [
        new HLF.TextureAsset("kitchen", "images/static/kitchen.jpg"),
        ...HLF.AssetFactory({"@1x": 1, "@2x": 1}, (i) => new HLF.TextureAtlasAsset(`kitchen-${i}`)),
        luna,
    ],
    luna
};


export function getShelfItemAssets(item: ItemModel) {
    switch (item.itemType) {
        case ItemType.Puzzle:
            return [
                puzzleImages[(item.itemData as PuzzleModel).puzzleType],
                puzzleFrames[(item.itemData as PuzzleModel).puzzleFrame],
            ];
        default:
            return [];
    }
}

export function updateStateAssets(state: StateName, data?: ItemData) {
    switch (state) {
        case StateName.Home:
            HLF.AssetMap.addAssetGroup(state, [
                ...Assets.common,
                ...Assets.home,
                ...GameManager.instance.getItems().reduce((a, t) => {
                    a.push(...getShelfItemAssets(t));
                    return a
                }, [] as HLF.IAsset<any>[])
            ]);
            break;
        case StateName.Robot:
        case StateName.Movie:
        case StateName.Monster:
            HLF.AssetMap.addAssetGroup(state, [
                ...Assets.common,
                ...(data ? [
                    ...assemblyPackages[(data as AssemblyModel<any>).assemblyType],
                    assemblyBackground[(data as AssemblyModel<any>).assemblyType],
                ] : [])
            ]);
            break;
        case StateName.Puzzle:
            HLF.AssetMap.addAssetGroup(state, [
                ...Assets.common,
                ...Assets.puzzleMiniGame,
                ...(data ? [
                    ...puzzleItems[(data as PuzzleModel).puzzleType],
                    puzzleImages[(data as PuzzleModel).puzzleType],
                ] : [])
            ]);
            break;
        case StateName.Kitchen:
            HLF.AssetMap.addAssetGroup(state, [
                ...Assets.common,
                ...Assets.cookingMiniGame,
                ...(data ? cookingAssets[(data as CookingModel).cookingType] : [])
            ]);
    }
}
